<template>
  <div class="diagnostic-appt-survey-wrapper">
    <!-- <div class="text-header-4 mb-12px">Select all that apply</div> -->

    <transition name="fade-faster" mode="out-in">
      <div :key="currentOptionsKey">
        <div class="diagnostic-survey-steps-wrapper">
          <survey-step
            v-for="option in displayedOptions"
            :key="option"
            :text="option"
            :has-arrow="isOnFirstStep"
            :icon="getSurveyIcon(option)"
            :selected="selectedAnswers.includes(option)"
            @click.native="() => handleOptionSelected(option)"
          />
        </div>

        <div
          v-if="
            selectedAnswers.includes($t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.text'))
          "
          class="warning-code-input-wrapper"
        >
          <div class="text-header-4 mb-12px">{{ $t('selectServiceStep.diagnosticAppointment.enterWarningCode') }}</div>
          <el-input
            :placeholder="$t('selectServiceStep.diagnosticAppointment.warningCodePlaceholder')"
            v-model="warningCode"
          />
        </div>
      </div>
    </transition>

    <transition name="fade-faster">
      <div v-if="showNextStepButton" class="sticked-to-bottom-button-wrapper">
        <div class="content-container">
          <button @click.prevent="completeSurvey" class="primary-btn full-width">
            {{ $t('general.next') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import SurveyStep from '@/components/select-service-step/SurveyStep';

export default {
  name: 'DiagnosticAppointmentSurvey',
  components: {
    SurveyStep,
  },
  data() {
    return {
      surveyData: [],
      currentOptionsKey: 0,
      displayedOptions: [],
      isOnFirstStep: true,
      isMultiselect: false,
      selectedAnswers: [],
      warningCode: '',
    };
  },
  mounted() {
    // We need to move it into the mounted hook because we are going to refer to "this" for the i18n instance translation method
    this._populateSurveyData();
    this.displayedOptions = this.surveyData.map((step) => step.problem);

    EventBus.$on('survey-step-back', () => {
      if (this.isOnFirstStep) {
        this.$emit('display-select-service-substep');
        return;
      }

      this._surveyGoBack();
    });
  },
  computed: {
    showNextStepButton() {
      return !this.isOnFirstStep && this.selectedAnswers.length > 1;
    },
  },
  methods: {
    _populateSurveyData() {
      this.surveyData = [
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.vehicleWontStart.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.vehicleWontStart.tellUsMore'),
          multiselect: true,
          possibleAnswers: [
            {
              answer: this.$t(
                'selectServiceStep.diagnosticAppointment.survey.vehicleWontStart.turnedKeyNothingHappens',
              ),
              subAnswers: [],
            },
            {
              answer: this.$t(
                'selectServiceStep.diagnosticAppointment.survey.vehicleWontStart.engineSoundsLikeItWillStart',
              ),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.vehicleWontStart.clickingSound'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.describeWarning'),
          multiselect: true,
          possibleAnswers: [
            {
              icon: 'engine_light',
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.engineLight'),
              subAnswers: [],
            },
            {
              icon: 'oil_pressure',
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.oilPressure'),
              subAnswers: [],
            },
            {
              icon: 'battery',
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.battery'),
              subAnswers: [],
            },
            {
              icon: 'tire_warning',
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.tireWarning'),
              subAnswers: [],
            },
            {
              icon: 'abs',
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.abs'),
              subAnswers: [],
            },
            { icon: 'other', answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.describeSound'),
          multiselect: true,
          possibleAnswers: [
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.squeakySound'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.rumbling'), subAnswers: [] },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.clicking'), subAnswers: [] },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.rattling'), subAnswers: [] },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.strangeNoise.bangingSound'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.carSmellsWeird.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.carSmellsWeird.describeSmell'),
          multiselect: false,
          possibleAnswers: [
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.carSmellsWeird.gasSmell'),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.carSmellsWeird.burntSmell'),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.carSmellsWeird.rottenEggs'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeSmoke.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeSmoke.describeSmoke'),
          multiselect: true,
          possibleAnswers: [
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeSmoke.underTheHood'),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeSmoke.somewhereOutside'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeALeak.text'),
          headerTitle: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeALeak.describeFluid'),
          multiselect: false,
          possibleAnswers: [
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeALeak.darkFluidColor'),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeALeak.insideTheVehicle'),
              subAnswers: [],
            },
            {
              answer: this.$t('selectServiceStep.diagnosticAppointment.survey.iSeeALeak.underTheVehicle'),
              subAnswers: [],
            },
            { answer: this.$t('selectServiceStep.diagnosticAppointment.survey.other') },
          ],
        },
        {
          problem: this.$t('selectServiceStep.diagnosticAppointment.survey.other'),
        },
      ];
    },
    _surveyGoBack() {
      this.currentOptionsKey -= 1;
      this.selectedAnswers = [];
      this.isOnFirstStep = true;
      this.displayedOptions = this.surveyData.map((step) => step.problem);
      this.$emit('header-title-change', this.$t('selectServiceStep.diagnosticAppointment.whatBringsYouIn'));
    },
    _goToDiagnosticAdditionalInfoSubstep() {
      this.$emit('display-diagnostic-additional-info-substep', {
        answers: this.warningCode ? [...this.selectedAnswers, this.warningCode] : this.selectedAnswers,
      });
    },
    _populateAnswersFromFirstStepSelectedProblem(selectedProblem) {
      const currentProblemData = this.surveyData.find(({ problem }) => problem === selectedProblem);

      this.displayedOptions = currentProblemData.possibleAnswers.map(({ answer }) => answer);
      this.currentOptionsKey += 1;
      this.selectedAnswers.push(selectedProblem);
      this.isOnFirstStep = false;
      this.$emit('header-title-change', currentProblemData.headerTitle);
    },
    getSurveyIcon(option) {
      if (this.isOnFirstStep) return null;

      if (
        this.selectedAnswers.includes(
          this.$t('selectServiceStep.diagnosticAppointment.survey.warningLightTurnedOn.text'),
        ) &&
        option === this.$t('selectServiceStep.diagnosticAppointment.survey.other')
      ) {
        return 'other';
      }

      const currentOptionsData = this.surveyData.find((data) => {
        return data.possibleAnswers.some(({ answer }) => answer === option);
      });

      const optionData = currentOptionsData.possibleAnswers.find(({ answer }) => answer === option);

      return optionData.icon || null;
    },
    handleOptionSelected(option) {
      if (this.isOnFirstStep && option.toLowerCase() === this.$t('selectServiceStep.diagnosticAppointment.survey.other').toLowerCase()) {
        this._goToDiagnosticAdditionalInfoSubstep();
        return;
      }

      if (this.isOnFirstStep) {
        this.$posthog.capture('serviceSelection_click_diagnosticProblemOptionButton');
        this._populateAnswersFromFirstStepSelectedProblem(option);
        return;
      }

      if (!this.selectedAnswers.includes(option)) {
        this.selectedAnswers.push(option);
        return;
      }

      this.selectedAnswers = this.selectedAnswers.filter((answer) => answer !== option);
    },
    completeSurvey() {
      this.$posthog.capture('serviceSelection_click_diagnosticProblemNextButton');
      this._goToDiagnosticAdditionalInfoSubstep();
    },
  },
};
</script>
